import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Layers, DollarSign, Archive, ClipboardList, Settings } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { getEffectivePermissions, PERMISSIONS } from '../auth/permissions';

const Container = styled.div`
  padding: 2rem clamp(1rem, 5vw, 3rem);
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;

  @media (min-width: 768px) {
    gap: 2rem;
  }
`;

const Card = styled.div`
  background: var(--white-a15);
  backdrop-filter: blur(16px);
  border: 1px solid var(--white-a20);
  border-radius: 16px;
  padding: 1.75rem;
  box-shadow: 0 6px 18px var(--shadow-a08);
  display: flex;
  align-items: center;
  gap: 1rem;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;

  &:hover {
    transform: translateY(-4px);
    border-color: var(--orange-a45);
    box-shadow: 0 12px 32px var(--shadow-a15);
  }

  &:focus,
  &:focus-visible {
    outline: 2px solid var(--orange-a60);
    outline-offset: 4px;
  }
`;

const Label = styled.span`
  font-weight: 600;
  font-size: 1rem;
  color: var(--green-900);
  text-shadow: 0 1px 1px var(--shadow-a05);
`;

const HomePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    document.title = 'The Vault – Home';
  }, []);

  if (!user) return null;

  const effective = getEffectivePermissions(user, {
    mockAdmin: process.env.REACT_APP_MOCK_ADMIN_USER === 'true',
  });
  const perms = effective.permissions;

  const sections = [
    ...(perms.has(PERMISSIONS.DataTablesRead)
      ? [
          { label: 'Commodities', icon: <Layers size={24} />, path: '/commodities' },
        ]
      : []),
    ...(perms.has(PERMISSIONS.ChargesRead)
      ? [
          { label: 'Charges', icon: <DollarSign size={24} />, path: '/charge-by-style' },
        ]
      : []),
    ...(perms.has(PERMISSIONS.PackagingIndex)
      ? [
          { label: 'Package Index', icon: <Archive size={24} />, path: '/package-index' },
        ]
      : []),
    ...(perms.has(PERMISSIONS.CustomerSpecsIndex)
      ? [
          { label: 'Customer Specifications', icon: <ClipboardList size={24} />, path: '/CustomerSpecs/Index' },
        ]
      : []),
    { label: 'Settings', icon: <Settings size={24} />, path: '/settings' },
  ];

  return (
    <main>
      <Container>
        {sections.map(({ label, icon, path }) => (
          <Card
            key={label}
            role="button"
            tabIndex={0}
            aria-label={`Go to ${label}`}
            onClick={() => navigate(path)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate(path);
              }
            }}
          >
            {icon}
            <Label>{label}</Label>
          </Card>
        ))}
      </Container>
    </main>
  );
};

export default HomePage;
